import type { Loan, LoanStage } from '@/types'
import { bookStage, requestedCommitment } from './helpers'

export type TrackerStatus = 'completed' | 'current' | 'upcoming'

export type TrackerStageId =
  | 'Intake'
  | 'Triage'
  | 'Pre-Screen'
  | 'Quote'
  | 'Underwriting'
  | 'Term Sheet'
  | 'Committee'
  | 'Closing'
  | 'Funded'

export type TrackerStageDef = {
  id: TrackerStageId
  label: string
  /** The phase a stage sits in, where the desk names one ('Underwriting'
   *  runs while a term sheet is signed). Shown under the stage, never
   *  instead of it. */
  sublabel?: string
  /** Canonical loan stage this tile filters to. Intake is pre-loan. */
  loanStage: LoanStage | null
}

/**
 * Visual origination tracker. `loanStage` is the live book field the tile
 * counts and filters.
 *
 * Tiles follow the canonical stage order (LOAN_STAGES / STAGE_ORDER) and carry
 * the stage's own name — the short print names the Pipeline Report bands use.
 * The rail used to title Term Sheet Signed 'Underwriting' and draw it BEFORE
 * 'Term Sheet' (= Issued), so a deal moving Issued → Signed walked backwards
 * on the rail and Calloway read 'Term Sheet Signed' in the grid but
 * 'Underwriting' here (OVR-16, CRE-14, LPR-20). The underwriting window is
 * kept as the Signed tile's sub-label.
 */
export const TRACKER_STAGES: readonly TrackerStageDef[] = [
  { id: 'Intake', label: 'Intake', loanStage: null },
  { id: 'Triage', label: 'Triage', loanStage: 'Triage' },
  { id: 'Pre-Screen', label: 'Pre-screen', loanStage: 'Pre-screen Indication' },
  { id: 'Quote', label: 'Quote', loanStage: 'Post-screen Quote' },
  { id: 'Term Sheet', label: 'TS Issued', loanStage: 'Term Sheet Issued' },
  { id: 'Underwriting', label: 'TS Signed', sublabel: 'Underwriting', loanStage: 'Term Sheet Signed' },
  { id: 'Committee', label: 'IC Approved', loanStage: 'IC / Committee Approved' },
  { id: 'Closing', label: 'Rate Lock', loanStage: 'Rate Lock' },
  { id: 'Funded', label: 'Funded', loanStage: 'Funded' },
] as const

export type TrackerStat = TrackerStageDef & {
  count: number
  volume: number
  status: TrackerStatus
}

export function formatTrackerMoney(value: number): string {
  if (!value) return '$0'
  const abs = Math.abs(value)
  if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  return `$${value.toLocaleString()}`
}

export function trackerStageForLoanStage(stage: string): TrackerStageDef | undefined {
  return TRACKER_STAGES.find((entry) => entry.loanStage === stage)
}

/**
 * Which tile is "in progress" for the book.
 * A selected stage wins; otherwise the busiest non-funded stage, later
 * winning ties so the glow sits at the furthest concentration of work.
 */
export function resolveTrackerFocus(counts: ReadonlyArray<{ id: TrackerStageId; count: number }>, activeStage: string): TrackerStageId {
  if (activeStage !== 'All') {
    const selected = trackerStageForLoanStage(activeStage) ?? TRACKER_STAGES.find((entry) => entry.id === activeStage)
    if (selected) return selected.id
  }

  let bestId: TrackerStageId = TRACKER_STAGES[0].id
  let bestCount = 0
  for (const row of counts) {
    if (row.id === 'Funded' || row.count === 0) continue
    if (row.count >= bestCount) {
      bestCount = row.count
      bestId = row.id
    }
  }
  if (bestCount > 0) return bestId

  const funded = counts.find((row) => row.id === 'Funded')
  return funded && funded.count > 0 ? 'Funded' : TRACKER_STAGES[0].id
}

export function trackerStatus(index: number, focusIndex: number): TrackerStatus {
  if (index < focusIndex) return 'completed'
  if (index === focusIndex) return 'current'
  return 'upcoming'
}

export function buildTrackerStats(loans: Loan[], activeStage: string): {
  stats: TrackerStat[]
  total: number
  totalVolume: number
  focusIndex: number
} {
  const counts = new Map<string, { count: number; volume: number }>()
  for (const loan of loans) {
    // A live deal counts where it is; a dead one where it died, so the same
    // rail serves the dead-deal book ("Dead by stage at death").
    const key = bookStage(loan)
    const entry = counts.get(key) ?? { count: 0, volume: 0 }
    entry.count += 1
    // One basis for every deal: the total commitment (OVR-19).
    entry.volume += requestedCommitment(loan) ?? 0
    counts.set(key, entry)
  }

  const raw = TRACKER_STAGES.map((def) => {
    const entry = def.loanStage ? counts.get(def.loanStage) ?? { count: 0, volume: 0 } : { count: 0, volume: 0 }
    return { id: def.id, count: entry.count, volume: entry.volume }
  })

  const focusId = resolveTrackerFocus(raw, activeStage)
  const focusIndex = Math.max(0, TRACKER_STAGES.findIndex((def) => def.id === focusId))

  const stats: TrackerStat[] = TRACKER_STAGES.map((def, index) => {
    const entry = raw[index]
    return {
      ...def,
      count: entry.count,
      volume: entry.volume,
      status: trackerStatus(index, focusIndex),
    }
  })

  return {
    stats,
    total: loans.length,
    totalVolume: loans.reduce((sum, loan) => sum + (requestedCommitment(loan) ?? 0), 0),
    focusIndex,
  }
}

export function segmentTone(fromIndex: number, focusIndex: number): TrackerStatus {
  if (fromIndex < focusIndex) return 'completed'
  if (fromIndex === focusIndex) return 'current'
  return 'upcoming'
}
